import React, { useState } from "react";
import { Tag, Tooltip } from "antd";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import IssueKeyBadge from "../../issues/components/IssueKeyBadge";
import UserAvatar from "../../components/UserAvatar";
import {
  ISSUE_TYPES,
  PRIORITY_OPTIONS,
  STATUS_CATEGORY_COLORS,
} from "../../constants/projects.constants";

import type {
  Issue,
  Status,
} from "../../../../core/data/redux/projects/types";

interface BacklogListProps {
  issues: Issue[];
  statuses: Status[];
  projectKey: string;
  canCreateIssue?: boolean;
  onCreateIssueClick: () => void;
}

const BacklogList: React.FC<BacklogListProps> = ({
  issues,
  statuses,
  projectKey,
  canCreateIssue = true,
  onCreateIssueClick,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const droppable = useDroppable({
    id: "backlog-target",
    data: { backlogTarget: true },
  });

  const itemIds = issues.map((i) => `issue-${i.id}`);

  return (
    <div className="card">
      <div className="card-header bg-white border-bottom">
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-dark"
            onClick={() => setCollapsed((c) => !c)}
          >
            <i
              className={collapsed ? "ti ti-chevron-right" : "ti ti-chevron-down"}
            />
          </button>
          <span className="fw-semibold">Backlog</span>
          <span className="text-muted small">
            ({issues.length} issue{issues.length === 1 ? "" : "s"})
          </span>
          {canCreateIssue && (
            <div className="ms-auto">
              <button
                type="button"
                className="btn btn-sm btn-info"
                onClick={onCreateIssueClick}
              >
                <i className="ti ti-plus me-1" />
                Create issue
              </button>
            </div>
          )}
        </div>
      </div>

      {!collapsed && (
        <div ref={droppable.setNodeRef} className="card-body p-0">
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            {issues.length === 0 ? (
              <div
                className="text-center text-muted small d-flex align-items-center justify-content-center"
                style={{
                  minHeight: 80,
                  margin: 8,
                  background: droppable.isOver
                    ? "rgba(59, 130, 246, 0.08)"
                    : "transparent",
                  borderRadius: 8,
                  border: droppable.isOver
                    ? "2px dashed #3B82F6"
                    : "2px dashed #e2e8f0",
                  transition: "all .15s",
                }}
              >
                <i className="ti ti-arrows-move me-1" />
                Drag issues here to move them to the backlog
              </div>
            ) : (
              <ul className="list-unstyled mb-0">
                {issues.map((issue) => (
                  <BacklogIssueRow
                    key={issue.id}
                    issue={issue}
                    projectKey={projectKey}
                    statuses={statuses}
                  />
                ))}
              </ul>
            )}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

interface BacklogIssueRowProps {
  issue: Issue;
  projectKey: string;
  statuses: Status[];
}

const BacklogIssueRow: React.FC<BacklogIssueRowProps> = ({
  issue,
  projectKey,
  statuses,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: `issue-${issue.id}`,
      data: { issueId: issue.id, issueRow: true },
    });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  const handleStyle: React.CSSProperties = {
    cursor: isDragging ? "grabbing" : "grab",
    fontSize: 16,
    color: "#94a3b8",
    flexShrink: 0,
    padding: "6px 4px",
    userSelect: "none",
    WebkitUserSelect: "none",
    touchAction: "none",
  };

  const typeMeta = ISSUE_TYPES.find((t) => t.value === issue.type);
  const priorityMeta = PRIORITY_OPTIONS.find((p) => p.value === issue.priority);
  const status = statuses.find((s) => Number(s.id) === Number(issue.status_id));

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
      onMouseEnter={(e) => {
        if (isDragging) return;
        e.currentTarget.style.background = "#f8fafc";
      }}
      onMouseLeave={(e) => {
        if (isDragging) return;
        e.currentTarget.style.background = "transparent";
      }}
    >
      <span
        {...attributes}
        {...listeners}
        style={handleStyle}
        className="drag-grip"
        title="Drag to move"
        aria-label="Drag handle"
      >
        <i className="ti ti-grip-vertical" />
      </span>
      {typeMeta && (
        <span
          className="d-inline-block rounded-circle"
          style={{ width: 8, height: 8, background: typeMeta.color }}
          title={typeMeta.label}
        />
      )}
      <IssueKeyBadge projectKey={projectKey} issueKey={issue.issue_key} />
      <span className="flex-grow-1 text-truncate">{issue.title}</span>
      {priorityMeta && (
        <span
          className="d-inline-block rounded-circle"
          style={{ width: 8, height: 8, background: priorityMeta.color }}
          title={`Priority: ${priorityMeta.label}`}
        />
      )}
      {status && (
        <Tag
          color="default"
          style={{
            background:
              status.color || STATUS_CATEGORY_COLORS[status.category],
            color: "#fff",
            border: "none",
            margin: 0,
          }}
        >
          {status.name}
        </Tag>
      )}
      {issue.assignee_snapshot?.name && (
        <Tooltip title={issue.assignee_snapshot.name}>
          <span>
            <UserAvatar
              name={issue.assignee_snapshot.name}
              img={issue.assignee_snapshot.img}
              size={22}
            />
          </span>
        </Tooltip>
      )}
    </li>
  );
};

export default BacklogList;
